import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatRange } from "@/lib/format";
import { Heart, Search, Filter } from "lucide-react";

export const Route = createFileRoute("/_authenticated/membros/negocios/")({
  head: () => ({
    meta: [
      { title: "Biblioteca de negócios — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LibraryPage,
});

const OPS = [
  { key: "servico", label: "Serviço" },
  { key: "produto", label: "Produto" },
  { key: "digital", label: "Digital" },
  { key: "hibrido", label: "Híbrido" },
];
const DIFF = [
  { key: "baixa", label: "Baixa" },
  { key: "media", label: "Média" },
  { key: "alta", label: "Alta" },
];

function LibraryPage() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("");
  const [maxInv, setMaxInv] = useState<number>(0);
  const [op, setOp] = useState<string>("");
  const [diff, setDiff] = useState<string>("");
  const [scal, setScal] = useState<string>("");
  const [wfh, setWfh] = useState(false);
  const [online, setOnline] = useState(false);
  const [onsite, setOnsite] = useState(false);
  const [team, setTeam] = useState<"any" | "solo" | "team">("any");

  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await supabase.from("categories").select("id,slug,name").order("sort_order")).data ?? [],
  });

  const { data: businesses } = useQuery({
    queryKey: ["businesses"],
    queryFn: async () =>
      (
        await supabase
          .from("businesses")
          .select("*, category:categories(id,slug,name)")
          .eq("active", true)
          .order("sort_order")
      ).data ?? [],
  });

  const { data: favIds } = useQuery({
    queryKey: ["favorite-ids"],
    queryFn: async () => {
      const { data } = await supabase.from("favorites").select("business_id");
      return new Set((data ?? []).map((f) => f.business_id as string));
    },
  });

  const filtered = useMemo(() => {
    return (businesses ?? []).filter((b: any) => {
      if (q && !b.title.toLowerCase().includes(q.toLowerCase()) && !(b.summary ?? "").toLowerCase().includes(q.toLowerCase())) return false;
      if (category && b.category?.slug !== category) return false;
      if (maxInv && b.investment_min_cents != null && b.investment_min_cents > maxInv) return false;
      if (op && b.operation_type !== op) return false;
      if (diff && b.difficulty !== diff) return false;
      if (scal && b.scalability !== scal) return false;
      if (wfh && !b.work_from_home) return false;
      if (online && !b.work_online) return false;
      if (onsite && !b.on_site) return false;
      if (team === "solo" && b.needs_team) return false;
      if (team === "team" && !b.needs_team) return false;
      return true;
    });
  }, [businesses, q, category, maxInv, op, diff, scal, wfh, online, onsite, team]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Biblioteca de negócios</h1>
        <p className="mt-1 text-sm text-[var(--gray-ink)]">
          Explore modelos reais. Use os filtros para encontrar o que combina com você.
        </p>
      </div>

      <div className="rounded-2xl border border-black/5 bg-white p-5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gray-ink)]" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou palavra-chave..."
            className="w-full rounded-md border border-black/10 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[var(--green)]"
          />
        </div>

        <details className="mt-4 rounded-md bg-[var(--gray-soft)]">
          <summary className="flex cursor-pointer items-center gap-2 px-4 py-2 text-sm font-semibold">
            <Filter size={14} /> Filtros
          </summary>
          <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
            <FilterSelect label="Categoria" value={category} onChange={setCategory} options={[{ value: "", label: "Todas" }, ...(cats ?? []).map((c) => ({ value: c.slug, label: c.name }))]} />
            <FilterSelect
              label="Investimento inicial (até)"
              value={String(maxInv)}
              onChange={(v) => setMaxInv(Number(v))}
              options={[
                { value: "0", label: "Sem limite" },
                { value: "100000", label: "Até R$ 1 mil" },
                { value: "300000", label: "Até R$ 3 mil" },
                { value: "500000", label: "Até R$ 5 mil" },
                { value: "1000000", label: "Até R$ 10 mil" },
              ]}
            />
            <FilterSelect label="Tipo de operação" value={op} onChange={setOp} options={[{ value: "", label: "Todos" }, ...OPS.map((o) => ({ value: o.key, label: o.label }))]} />
            <FilterSelect label="Dificuldade" value={diff} onChange={setDiff} options={[{ value: "", label: "Todas" }, ...DIFF.map((d) => ({ value: d.key, label: d.label }))]} />
            <FilterSelect label="Escalabilidade" value={scal} onChange={setScal} options={[{ value: "", label: "Todas" }, ...DIFF.map((d) => ({ value: d.key, label: d.label }))]} />
            <FilterSelect label="Equipe" value={team} onChange={(v) => setTeam(v as any)} options={[{ value: "any", label: "Tanto faz" }, { value: "solo", label: "Sozinho(a)" }, { value: "team", label: "Precisa de equipe" }]} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={wfh} onChange={(e) => setWfh(e.target.checked)} /> Trabalha em casa</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} /> Trabalha online</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onsite} onChange={(e) => setOnsite(e.target.checked)} /> No local do cliente</label>
          </div>
        </details>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((b: any) => (
          <BusinessCard key={b.id} b={b} isFav={!!favIds?.has(b.id)} />
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full text-sm text-[var(--gray-ink)]">Nenhum negócio encontrado com esses filtros.</p>
        )}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--green)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function BusinessCard({ b, isFav }: { b: any; isFav: boolean }) {
  const qc = useQueryClient();
  async function toggleFav(e: React.MouseEvent) {
    e.preventDefault();
    const { data: userRes } = await supabase.auth.getUser();
    const uid = userRes.user?.id;
    if (!uid) return;
    if (isFav) {
      await supabase.from("favorites").delete().eq("user_id", uid).eq("business_id", b.id);
    } else {
      await supabase.from("favorites").insert({ user_id: uid, business_id: b.id });
    }
    qc.invalidateQueries({ queryKey: ["favorite-ids"] });
    qc.invalidateQueries({ queryKey: ["favorites-preview"] });
    qc.invalidateQueries({ queryKey: ["favorites-full"] });
  }
  return (
    <div className="flex flex-col rounded-2xl border border-black/5 bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--green)]">
            {b.category?.name ?? "—"}
          </p>
          <h3 className="mt-1 font-display text-lg font-extrabold leading-tight">{b.title}</h3>
        </div>
        <button
          onClick={toggleFav}
          className={`shrink-0 rounded-full p-2 transition ${
            isFav ? "bg-[var(--green)]/10 text-[var(--green)]" : "text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
          }`}
          aria-label="Favoritar"
        >
          <Heart size={16} fill={isFav ? "currentColor" : "none"} />
        </button>
      </div>
      <p className="mt-2 line-clamp-3 text-sm text-[var(--gray-ink)]">{b.summary}</p>
      <dl className="mt-4 grid grid-cols-2 gap-2 text-xs text-[var(--gray-ink)]">
        <div>
          <dt className="font-semibold text-[var(--navy)]">Investimento</dt>
          <dd>{formatRange(b.investment_min_cents, b.investment_max_cents)}</dd>
        </div>
        <div>
          <dt className="font-semibold text-[var(--navy)]">Dificuldade</dt>
          <dd className="capitalize">{b.difficulty ?? "—"}</dd>
        </div>
        <div>
          <dt className="font-semibold text-[var(--navy)]">Escalabilidade</dt>
          <dd className="capitalize">{b.scalability ?? "—"}</dd>
        </div>
        <div>
          <dt className="font-semibold text-[var(--navy)]">Operação</dt>
          <dd className="capitalize">{b.operation_type ?? "—"}</dd>
        </div>
      </dl>
      <Link
        to="/membros/negocios/$slug"
        params={{ slug: b.slug }}
        className="mt-4 inline-flex items-center justify-center rounded-md bg-[var(--navy)] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--navy)]/90"
      >
        Ver negócio
      </Link>
    </div>
  );
}
